import { CellClassParams, ColDef, ICellRendererParams } from "ag-grid-community";
import { ColumnTypes, getDateKey } from "../shipment-review-grid";
import { isInErrorState } from "./inventory";
import { Editors } from "constants/product-collection";
import { isAvailableDate } from "./isAvailableDate";
import { formatDateRange } from "./formatDate";
import { extractLineItemIdByRowNode } from "./extractLineItemIdByRowNode";
import { IItem } from "../types";
import { toSafeDate } from "helpers/dates";

export const parseLineItemId = (lineItemIdStr: string): string | undefined => {
    // First id before `_` is line item id. We know this for a fact.
    const lineItemIdMatch = lineItemIdStr.match(/^([a-zA-Z0-9]+)_/i);
    const lineItemId = lineItemIdMatch?.[1];
    if (!lineItemId) {
        return;
    }

    return lineItemId;
};

const deliveryWindowCellClass = (
    dateStart: Date,
    dateEnd: Date,
    options: { isBuildOrderEnabled?: boolean },
    params: CellClassParams,
): string[] => {
    const { node, colDef } = params;
    const { allLeafChildren } = node;
    const classes = ["cell--delivery-window"];
    const { enforceAvailableUntil, item, minimumDeliveryWindow } = (allLeafChildren?.[0]?.data ?? {}) as IItem;

    if (!item) {
        return classes;
    }

    const availableFrom = toSafeDate(item.product.available_from);
    const availableUntil = toSafeDate(item.product.available_until);

    const dates = {
        shipStart: dateStart,
        shipEnd: dateEnd,
    };

    let isInError = false;

    if (allLeafChildren) {
        for (const child of allLeafChildren) {
            const { item, shipStart: childShipStart, shipEnd: childShipEnd } = child.data;

            if (getDateKey(childShipStart, childShipEnd) !== colDef.colId) {
                continue;
            }

            const { quantity, prebook, inventory } = item;

            isInError = isInErrorState(inventory?.available, quantity, prebook);
            if (isInError) {
                break;
            }
        }
    }

    const availableDates = {
        availableFrom,
        availableUntil,
        enforceAvailableUntil,
        enforcePastShipStart: true,
        minimumDeliveryWindow,
    };

    if (dates?.shipStart) {
        if ((!options.isBuildOrderEnabled || isAvailableDate(dates, availableDates)) && !!item?.available) {
            classes.push("cell-enabled");
        } else {
            classes.push("cell-disabled");
        }
    }

    if (extractLineItemIdByRowNode(colDef, node)) {
        classes.push("cell--delivery-window__clickable");
    }

    if (isInError) {
        classes.push("quantityError");
    }

    return classes;
};

export const buildDeliveryWindowColDef = (
    dateStart: Date | undefined,
    dateEnd: Date | undefined,
    options: { isBuildOrderEnabled?: boolean } = {},
): ColDef => {
    const dateKey = getDateKey(dateStart, dateEnd);
    return {
        headerName: formatDateRange(dateStart, dateEnd),
        colId: dateKey,
        type: ColumnTypes.DeliveryWindow,
        aggFunc: "sum",
        cellRendererSelector: (params: ICellRendererParams) => {
            if (params.node.rowPinned === "bottom") {
                return {
                    component: Editors.PinnedFooterRowRenderer,
                };
            }
            return {
                component: Editors.ItemRenderer,
            };
        },
        cellClass: deliveryWindowCellClass.bind(this, dateStart, dateEnd, options),
        width: 123,
        valueGetter: ({ data }): number => {
            if (!data) {
                return 0;
            }
            const rowData = data as any;
            if (rowData?.pricing) {
                return rowData?.pricing[dateKey]?.quantity ?? 0;
            } else {
                return 0;
            }
        },
    };
};
